// Proyección isométrica y primitivas SVG del estudio (Fase 9.2).
//
// Coordenadas del mundo en "unidades de piso": x crece hacia abajo-derecha,
// y hacia abajo-izquierda y z hacia arriba. El observador mira desde +x +y, así
// que de una caja se ven la tapa, la cara y+d (frente izquierdo) y la cara
// x+w (frente derecho). Se dibuja de atrás hacia adelante (menor x+y primero).
import React, { createContext, useContext } from 'react';

const COS30 = Math.cos(Math.PI / 6);
export const Z_SCALE = 0.9;       // las alturas se ven un poco más bajas que el piso

export function makeProjector(scale) {
  return (x, y, z = 0) => [(x - y) * COS30 * scale, (x + y) * 0.5 * scale - z * Z_SCALE * scale];
}

const f = n => Math.round(n * 10) / 10;
export const toPoints = arr => arr.map(([a, b]) => `${f(a)},${f(b)}`).join(' ');

export const IsoContext = createContext(null);
export const useIso = () => useContext(IsoContext);

export function IsoProvider({ scale, theme, detail, children }) {
  const P = makeProjector(scale);
  return <IsoContext.Provider value={{ P, s: scale, theme, c: theme.colors, detail }}>{children}</IsoContext.Provider>;
}

/** Polígono a partir de puntos 3D. */
export function Poly({ pts, fill, stroke = 'none', sw = 0.6, opacity, className }) {
  const { P } = useIso();
  return (
    <polygon points={toPoints(pts.map(p => P(...p)))} fill={fill} stroke={stroke} strokeWidth={sw}
      opacity={opacity} className={className} strokeLinejoin="round" />
  );
}

/** Caja: tapa, frente izquierdo (y+d) y frente derecho (x+w). */
export function Box({ x, y, z = 0, w, d, h, top, left, right, stroke = 'rgba(0,0,0,0.25)', opacity }) {
  return (
    <g opacity={opacity}>
      <Poly pts={[[x, y + d, z], [x + w, y + d, z], [x + w, y + d, z + h], [x, y + d, z + h]]} fill={left} stroke={stroke} />
      <Poly pts={[[x + w, y, z], [x + w, y + d, z], [x + w, y + d, z + h], [x + w, y, z + h]]} fill={right} stroke={stroke} />
      <Poly pts={[[x, y, z + h], [x + w, y, z + h], [x + w, y + d, z + h], [x, y + d, z + h]]} fill={top} stroke={stroke} />
    </g>
  );
}

/** Caja con los colores de mueble del tema. */
export function Furniture(props) {
  const { c } = useIso();
  return <Box top={c.furnitureTop} left={c.furniture} right={c.furnitureSide} {...props} />;
}

/** Texto con halo para que se lea sobre cualquier fondo. */
export function Label({ at, text, size = 10, color, weight = 600, anchor = 'middle', dy = 0, opacity }) {
  const { P, c } = useIso();
  const [x, y] = P(...at);
  return (
    <text x={f(x)} y={f(y + dy)} fontSize={size} fontWeight={weight} fill={color || c.text} textAnchor={anchor}
      stroke={c.halo} strokeWidth={3} paintOrder="stroke" strokeLinejoin="round" opacity={opacity}
      style={{ pointerEvents: 'none' }}>
      {text}
    </text>
  );
}
